import { Logger } from '~~/helpers/logger';
import { useCategoryApi } from '~/utils/ct/composables/api/useCategoryApi';
import { CATEGORY_CUSTOM_FIELDS } from '~~/constants/commerceTools';
import type { ApiParams, Category, RawCustomField } from '~~/types/commercetools';
import { errorToString } from '~~/helpers/error/errorToString';
import { filterLocaleMapValue } from '~~/helpers/customFields';

function collapseHasProducts(categories: Category[], locale: string): Category[] {

  return categories.map(category => {

    if (category.custom?.customFieldsRaw) {

      category.custom.customFieldsRaw = category.custom.customFieldsRaw.map((field: RawCustomField) => {

        if (field.name === CATEGORY_CUSTOM_FIELDS.HAS_PRODUCTS) {

          const filtered = filterLocaleMapValue(field.value, new Set([locale]));

          if (filtered !== field.value) {

            return { ...field, value: (filtered as Record<string, unknown>)[locale] ?? field.value };

          }

        }

        return field;
      
      });
    
    }

    if (category.children?.length) {

      category.children = collapseHasProducts(category.children, locale);
    
    }

    return category;
  
  });

}

export function useCategory(id: string) {

  const { search: apiSearch } = useCategoryApi();
  const { languageAndCountry } = useLocalization();
  const categories: Ref<Category[]> = useState(`useCategory-categories-${id}`, () => []);
  const loading = useState(`useCategory-loading-${id}`, () => false);
  const error = useState<{
    search: null | string
  }>(`useCategory-error-${id}`, () => ({
    search: null,
  }));

  async function search(searchParams: ApiParams) {

    Logger.debug(`useCategory/${id}/search`, searchParams);
    try {

      loading.value = true;
      const data = await apiSearch(searchParams);
      categories.value = collapseHasProducts(data, languageAndCountry.value);
      error.value.search = null;
    
    } catch (err: unknown) {

      error.value.search = errorToString(err);
      Logger.error(`useCategory/${id}/search`, err);
    
    } finally {

      loading.value = false;
    
    }
  
  }

  return {
    search,
    loading: computed(() => loading.value),
    categories: computed(() => categories.value),
    error: computed(() => error.value),
  };

}
